import { Box, Chip, Typography, useTheme } from '@mui/material'
import { useMasterAgreements } from 'api/aws/deals/useMasterAgreements'
import { useOrganizationView } from 'api/aws/organizations_table/useOrganizationView'
import { renderColorByString } from 'components/DataGrid/renderColorByString'
import ViewAgreement from 'components/Deals/contracts/ViewAgreement'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { GROBxLoader } from 'theme/components'
import type { AgreementFamily, MasterAgreement } from 'utils/agreementVersioning'
import AgreementVersionView from './AgreementVersionView'

const familyColors: Record<AgreementFamily, string> = {
  MSA: 'rgb(107, 185, 240)',
  'MSA - Housing': 'rgb(80, 200, 160)',
  CSA: 'rgb(170, 130, 240)',
}

type Selection =
  | { type: 'agreement'; id: string; versionIndex: number }
  | { type: 'standalone'; id: string; PK: string; SK: string }

const MasterAgreementsPage = () => {
  const { PK } = useParams()
  const theme = useTheme()
  const { pinned } = useLayoutContext()
  const { organization } = useOrganizationView(PK)
  const { agreements, standaloneDeals, isLoading } = useMasterAgreements(PK)

  const [selection, setSelection] = useState<Selection | null>(null)

  // Resolve active agreement
  const activeAgreement: MasterAgreement | undefined =
    selection?.type === 'agreement'
      ? agreements.find(a => a.masterAgreementId === selection.id) || agreements[0]
      : undefined

  // If nothing is selected yet, default to first agreement (or first standalone if no agreements)
  const effectiveSelection: Selection | null =
    selection ??
    (agreements[0]
      ? { type: 'agreement', id: agreements[0].masterAgreementId, versionIndex: -1 }
      : standaloneDeals[0]
      ? { type: 'standalone', id: standaloneDeals[0].deal_uuid, PK: standaloneDeals[0].PK, SK: standaloneDeals[0].SK }
      : null)

  const resolvedAgreement: MasterAgreement | undefined =
    effectiveSelection?.type === 'agreement'
      ? agreements.find(a => a.masterAgreementId === effectiveSelection.id) || agreements[0]
      : undefined

  const resolvedVersionIndex =
    resolvedAgreement && effectiveSelection?.type === 'agreement'
      ? effectiveSelection.versionIndex >= 0 && effectiveSelection.versionIndex < resolvedAgreement.versions.length
        ? effectiveSelection.versionIndex
        : resolvedAgreement.versions.length - 1
      : -1

  const handleAgreementSelect = (id: string) => {
    setSelection({ type: 'agreement', id, versionIndex: -1 })
  }

  const handleStandaloneSelect = (deal: any) => {
    setSelection({ type: 'standalone', id: deal.deal_uuid, PK: deal.PK, SK: deal.SK })
  }

  const handleVersionSelect = (index: number) => {
    if (effectiveSelection?.type === 'agreement') {
      setSelection({ type: 'agreement', id: effectiveSelection.id, versionIndex: index })
    }
  }

  if (isLoading) {
    return <GROBxLoader open={true} message={'Loading Agreements'} />
  }

  if (agreements.length === 0 && standaloneDeals.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Typography sx={{ fontSize: 16, color: 'text.secondary', fontWeight: 500 }}>No agreements found</Typography>
        <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>
          This organization has no MSA, CSA, or related agreements.
        </Typography>
      </Box>
    )
  }

  const showSelector = agreements.length + standaloneDeals.length > 1

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Agreement / IO selector bar */}
      {showSelector && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            flexShrink: 0,
            flexWrap: 'wrap',
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mr: 1 }}>Agreements:</Typography>
          {agreements.map(agreement => {
            const isActive =
              effectiveSelection?.type === 'agreement' &&
              agreement.masterAgreementId === (resolvedAgreement?.masterAgreementId ?? '')
            const color = familyColors[agreement.agreementFamily]
            return (
              <Chip
                key={agreement.masterAgreementId}
                label={`${agreement.agreementFamily} — ${agreement.versions.length} version${
                  agreement.versions.length !== 1 ? 's' : ''
                }`}
                size="small"
                onClick={() => handleAgreementSelect(agreement.masterAgreementId)}
                sx={{
                  height: 26,
                  fontSize: 12,
                  fontWeight: 600,
                  bgcolor: isActive ? `${color}22` : 'transparent',
                  color: isActive ? color : 'text.secondary',
                  border: '1px solid',
                  borderColor: isActive ? color : 'divider',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: `${color}15` },
                }}
              />
            )
          })}
          {standaloneDeals.length > 0 && agreements.length > 0 && (
            <Box sx={{ width: 1, height: 16, borderLeft: '1px solid', borderColor: 'divider', mx: 0.5 }} />
          )}
          {standaloneDeals.map(deal => {
            const isActive = effectiveSelection?.type === 'standalone' && effectiveSelection.id === deal.deal_uuid
            const typeColor = renderColorByString(deal.type)
            return (
              <Chip
                key={deal.deal_uuid}
                label={deal.name || deal.type || 'IO'}
                size="small"
                onClick={() => handleStandaloneSelect(deal)}
                sx={{
                  height: 26,
                  fontSize: 12,
                  fontWeight: isActive ? 600 : 500,
                  bgcolor: isActive ? `${typeColor}22` : 'transparent',
                  color: isActive ? typeColor : 'text.secondary',
                  border: '1px solid',
                  borderColor: isActive ? typeColor : 'divider',
                  cursor: 'pointer',
                  maxWidth: 220,
                  '&:hover': { bgcolor: `${typeColor}15` },
                }}
              />
            )
          })}
        </Box>
      )}

      {/* Main content: versioned agreement OR standalone deal */}
      {effectiveSelection?.type === 'agreement' && resolvedAgreement && (
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <AgreementVersionView
            agreement={resolvedAgreement}
            selectedVersionIndex={resolvedVersionIndex}
            onVersionSelect={handleVersionSelect}
          />
        </Box>
      )}

      {effectiveSelection?.type === 'standalone' && (
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <ViewAgreement dealPK={effectiveSelection.PK} dealSK={effectiveSelection.SK} />
        </Box>
      )}
    </Box>
  )
}

export default MasterAgreementsPage
